import { axiosInstance } from "#services/axiosInstance.js";
import { useInfiniteQuery } from "react-query";

/**
 * Custom hook for infinite scrolling GET requests (supports your pagination format).
 *
 * @param {string} url - The API endpoint.
 * @param {string | string[]} queryKey - Unique query key for caching.
 * @param {object} [params={}] - Extra query params to send with the request.
 * @param {number} [pageSize=10] - Items per page.
 * @returns {object} React Query infinite query result.
 */
const useInfiniteGet = (url, queryKey, params = {}, pageSize = 10) => {
  const fetchPage = async ({ pageParam = 1 }) => {
    const response = await axiosInstance.get(url, {
      params: { ...params, page: pageParam, limit: pageSize },
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useInfiniteQuery([queryKey, params], fetchPage, {
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { currentPage = 1, totalPages = 1 } = lastPage;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    keepPreviousData: true,       //  Keep old data when fetching new
    refetchOnMount: true,         //  Auto-fetch when component mounts
    refetchOnWindowFocus: false,  //  Avoid refetching when tab changes
    staleTime: 0,                 //  Always fresh
  });

  // Combine all items from all pages
  const allItems = data?.pages?.flatMap((page) => page?.data || []) || [];

  //Total items (from backend or fallback to count)
  const totalItems = data?.pages?.[0]?.totalItems ?? allItems.length;

  // Current page (useful if needed)
  const currentPage = data?.pages?.[data.pages.length - 1]?.currentPage ?? 1;

  return {
    allItems,
    totalItems,
    currentPage,
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

export default useInfiniteGet;
